import { z } from 'zod';

export const judgeKindSchema = z.enum(['rubric', 'checklist']);
export type JudgeKind = z.infer<typeof judgeKindSchema>;

export const rubricScaleSchema = z.enum(['pass_fail', 'likert_5']);
export type RubricScale = z.infer<typeof rubricScaleSchema>;

export const qdrantContextSourceSchema = z.object({
	kind: z.literal('qdrant_chunks'),
	namespace: z.string().default('support'),
	topK: z.number().int().min(1).max(20).default(5),
});
export type QdrantContextSource = z.infer<typeof qdrantContextSourceSchema>;

export const accountSnapshotContextSourceSchema = z.object({
	kind: z.literal('account_snapshot'),
});
export type AccountSnapshotContextSource = z.infer<typeof accountSnapshotContextSourceSchema>;

export const toolOutputsContextSourceSchema = z.object({
	kind: z.literal('tool_outputs'),
	maxResults: z.number().int().min(1).max(50).default(12),
	maxCharsEach: z.number().int().min(100).max(4000).default(800),
});
export type ToolOutputsContextSource = z.infer<typeof toolOutputsContextSourceSchema>;

/**
 * Catch-all for context source kinds this UI does not know yet.
 *
 * The backend catalog evolves faster than this schema: `account_snapshot`
 * and `tool_outputs` were both added after the first version of this file
 * and every judge carrying them failed to parse, which blanked the whole
 * judges list instead of degrading that single row. Keeping unknown kinds
 * parseable means a new backend source can never take the view down again
 * — the form preserves them verbatim and shows them as read-only.
 */
export const unknownContextSourceSchema = z.object({ kind: z.string() }).passthrough();
export type UnknownContextSource = z.infer<typeof unknownContextSourceSchema>;

// Plain union, not discriminated: the catch-all overlaps every literal kind,
// so the first matching branch wins and anything malformed still parses.
export const contextSourceSchema = z.union([
	qdrantContextSourceSchema,
	accountSnapshotContextSourceSchema,
	toolOutputsContextSourceSchema,
	unknownContextSourceSchema,
]);
export type ContextSource = z.infer<typeof contextSourceSchema>;

/**
 * Type guard for the only source the judge form can edit. A plain
 * `kind === 'qdrant_chunks'` check does not narrow the union because the
 * catch-all also accepts that string.
 *
 * A malformed `qdrant_chunks` object falls through to the catch-all, so the
 * kind alone does not prove the shape either — the fields are checked before
 * narrowing, otherwise the form would read `namespace` off something that is
 * not a string. Missing fields never reach here: the schema defaults them.
 * An out-of-range `topK` does pass (it is still a number, and the form clamps
 * it on save); only a wrong runtime type is rejected.
 */
export const isQdrantContextSource = (source: ContextSource): source is QdrantContextSource =>
	source.kind === 'qdrant_chunks' &&
	typeof (source as Partial<QdrantContextSource>).namespace === 'string' &&
	Number.isInteger((source as Partial<QdrantContextSource>).topK);

export const judgeAppliesToSchema = z.object({
	modules: z.array(z.string()),
	agents: z.array(z.string()),
});
export type JudgeAppliesTo = z.infer<typeof judgeAppliesToSchema>;

const baseJudgeFields = {
	id: z.string(),
	name: z.string(),
	description: z.string(),
	enabled: z.boolean(),
	appliesTo: judgeAppliesToSchema,
	createdAt: z.string(),
	updatedAt: z.string(),
};

export const rubricJudgeSchema = z.object({
	...baseJudgeFields,
	kind: z.literal('rubric'),
	prompt: z.string(),
	scale: rubricScaleSchema,
	contextSources: z.array(contextSourceSchema).default([]),
});
export type RubricJudge = z.infer<typeof rubricJudgeSchema>;

export const checklistCriterionSchema = z.object({
	id: z.string(),
	label: z.string(),
	description: z.string(),
});
export type ChecklistCriterion = z.infer<typeof checklistCriterionSchema>;

export const checklistJudgeSchema = z.object({
	...baseJudgeFields,
	kind: z.literal('checklist'),
	criteria: z.array(checklistCriterionSchema).min(1),
});
export type ChecklistJudge = z.infer<typeof checklistJudgeSchema>;

export const judgeSchema = z.discriminatedUnion('kind', [rubricJudgeSchema, checklistJudgeSchema]);
export type Judge = z.infer<typeof judgeSchema>;

// ─── Verdicts / Evaluations ───────────────────────────────────────────────

export const rubricStatusSchema = z.enum(['passed', 'failed', '1', '2', '3', '4', '5']);
export type RubricStatus = z.infer<typeof rubricStatusSchema>;

export const rubricVerdictSchema = z.object({
	kind: z.literal('rubric'),
	judgeId: z.string(),
	judgeName: z.string(),
	scale: rubricScaleSchema,
	status: rubricStatusSchema,
	reasoning: z.string(),
	runAt: z.string(),
	durationMs: z.number().int().nonnegative(),
});
export type RubricVerdict = z.infer<typeof rubricVerdictSchema>;

export const checklistResultSchema = z.object({
	criterionId: z.string(),
	criterionLabel: z.string(),
	passed: z.boolean(),
	reasoning: z.string(),
});

export const checklistVerdictSchema = z.object({
	kind: z.literal('checklist'),
	judgeId: z.string(),
	judgeName: z.string(),
	results: z.array(checklistResultSchema),
	runAt: z.string(),
	durationMs: z.number().int().nonnegative(),
});
export type ChecklistVerdict = z.infer<typeof checklistVerdictSchema>;

export const externalEvaluationSourceSchema = z.enum(['intercom', 'human']);
export type ExternalEvaluationSource = z.infer<typeof externalEvaluationSourceSchema>;

export const externalEvaluationScaleSchema = z.enum(['1_to_5', 'thumbs', 'nps_0_10']);
export type ExternalEvaluationScale = z.infer<typeof externalEvaluationScaleSchema>;

export const externalEvaluationVerdictSchema = z.object({
	kind: z.literal('external_evaluation'),
	judgeId: z.string(),
	judgeName: z.string(),
	source: externalEvaluationSourceSchema,
	value: z.number().int(),
	scale: externalEvaluationScaleSchema,
	remark: z.string().nullable().optional(),
	metadata: z.record(z.unknown()).default({}),
	runAt: z.string(),
	durationMs: z.number().int().nonnegative(),
});
export type ExternalEvaluationVerdict = z.infer<typeof externalEvaluationVerdictSchema>;

export const evaluationSchema = z.discriminatedUnion('kind', [
	rubricVerdictSchema,
	checklistVerdictSchema,
	externalEvaluationVerdictSchema,
]);
export type Evaluation = z.infer<typeof evaluationSchema>;

// ─── Helpers ──────────────────────────────────────────────────────────────

export const isVerdictPassing = (e: Evaluation): boolean => {
	if (e.kind === 'rubric') {
		if (e.scale === 'pass_fail') return e.status === 'passed';
		const numeric = Number(e.status);

		return Number.isFinite(numeric) && numeric >= 4;
	}
	if (e.kind === 'external_evaluation') {
		if (e.scale === '1_to_5') return e.value >= 4;
		if (e.scale === 'thumbs') return e.value === 1;
		if (e.scale === 'nps_0_10') return e.value >= 9;
		return false;
	}
	return e.results.every((r) => r.passed);
};
